import { useQueries } from '@tanstack/react-query';

import { useApiClient } from '@/api/clients/useApiClient.util.ts';
import {
  CustomerPropertiesDto,
  CustomerStatusDto,
  ProcessCreationDefaultDataMatchDto,
} from '@/api/generated';
import { QueryKeyEnum } from '@/api/model/QueryKey.enum.ts';
import { processesCreateUtil } from '@/views/processesCreate/utils/processesCreate.util.ts';

type Return = {
  /** Some status is still loading, so the default match waits for it. */
  isLoading: boolean;
  /** The status of each matched customer by its id; absent while loading or when it failed to load. */
  statuses: Partial<Record<number, CustomerStatusDto>>;
};

/**
 * The statuses of the customers matched to the sender of an e-mail. The match does not carry the status,
 * so it is read from the customer, to tell the customers that cannot become the customer of a process.
 */
export const useGetMatchCustomerStatuses = (
  matches: ProcessCreationDefaultDataMatchDto[],
): Return => {
  const { customersApi } = useApiClient();
  const customerIds = [...new Set(matches.map(match => match.customerId))];

  return useQueries({
    combine: results => {
      const statuses: Partial<Record<number, CustomerStatusDto>> = {};
      results.forEach((result, index) => {
        if (result.data) statuses[customerIds[index]] = result.data;
      });
      return { isLoading: results.some(result => result.isPending), statuses };
    },
    // the details of the matched customer: the dialog reads the same query once the match is applied
    queries: customerIds.map(customerId => ({
      queryFn: () => processesCreateUtil.getCustomerDetails(customersApi, customerId),
      queryKey: [QueryKeyEnum.CUSTOMERS.DETAILS, customerId],
      select: (customer: CustomerPropertiesDto | undefined) => customer?.status,
    })),
  });
};
